/**
 * The history-writing save (backend #439, owner decision). Nothing rewrites a
 * user's past transactions without a stated count:
 *
 *   1. save with `apply_to_past: false` — writes the rule, touches no history;
 *   2. dry-run the SAVED rule by id for the exact, lock-aware, fill-only count;
 *   3. show that count and ask;
 *   4. only then PATCH `apply_to_past: true`, which the server runs as one batch;
 *   5. offer Undo on that batch, by the id the SAVE returned.
 *
 * The sequence runs only when the save would make the server backfill
 * (ruling 5, mirroring `save_triggers_backfill`); every other save is one
 * write that carries the draft's own flag, so a rename never asks and never
 * turns a stored `apply_to_past: true` off.
 */
import { useEffect, useRef, useState } from 'react'
import { keys } from '../../api/keys'
import { type Props } from '../../platform/analytics/analytics'
import { atRuleCap, RULE_CAP_REASON } from './ruleCap'
import { clampStart, localToday, windowStart } from './backfillWindow'
import { toWrite, type ReadRule, type RuleDraft } from './ruleWire'
import {
  categoryOf,
  IDLE,
  mayHaveCommitted,
  needsConfirm,
  NOT_APPLIED,
  NOT_CONFIRMED_APPLY,
  same,
  SaveError,
  saveFailureCopy,
  track,
  withoutPastCopy,
  type ConfirmStep,
  type Offer,
  type SaveStep,
} from './saveShared'
import type { RuleBase } from './useRuleBase'
import { fetchBackfillPreview, useCreateRule, listAllRules, renumberRules, type SaveResult } from './useRules'

export function useBuilderSave(shared: RuleBase, { onDone }: { onDone: () => void }) {
  const { client, update, toast, announceBackfill } = shared
  const create = useCreateRule()
  const [step, setStep] = useState<SaveStep>(IDLE)
  const [offer, setOffer] = useState<Offer | null>(null)
  /** Whether this builder session CREATED its rule — the toast says "Created"
   *  even when a Back-then-Save made the last write a PATCH. Only the builder
   *  reads it: the list switch words its own toast. */
  const created = useRef(false)
  /** The rule THIS builder session has already written. After step 1 a new
   *  rule exists; saving again from the same builder must PATCH it, never POST
   *  a duplicate that the name's uniqueness would then refuse with a 409.
   *  Scoped to one session: `begin`, `close`, `reset` and a finished save all
   *  clear it, so no later builder can inherit it and PATCH the wrong rule. */
  const written = useRef<ReadRule | null>(null)
  /** Bumped whenever a session ends. A `save` whose session has ended while
   *  it awaited touches none of the state above — it belongs to nobody. */
  const session = useRef(0)
  /** Sessions whose `onWritten` callback has already run, so a save still in flight when the session
   *  ended (a re-save after "Back to rule") cannot run it a second time. */
  const told = useRef(new Set<number>())
  /** The builder's `rule_created`, held until the stored `apply_to_past` is
   *  known: a save that asks writes `false` first and turns it on only once the
   *  user confirms, so the event waits for the answer. */
  const pendingCreated = useRef<Props | null>(null)
  // The page leaving with the question still open: the rule stands as step 1
  // wrote it, so report that rather than losing the creation.
  useEffect(
    () => () => {
      const props = pendingCreated.current
      pendingCreated.current = null
      if (props !== null) track('rule_created', { ...props, apply_to_past_enabled: false })
      // The page leaving ends the session: a write still in flight belongs to
      // nobody, and must report what it finds rather than touch dead state.
      session.current += 1
      // The same as `close` for a rule THIS session already wrote (step 1, or an earlier save): the caller
      // learns it stands, so a suggestion that opened the builder is dismissed. A first save still writing
      // has written nothing yet; its own completion fires the callback it captured. A re-save in flight is
      // both — `told` stops its completion firing the callback a second time. No toast: the page is gone.
      const cb = written.current === null ? null : onWritten.current
      const ending = session.current - 1
      written.current = null
      onWritten.current = null
      if (cb !== null) told.current.add(ending)
      cb?.()
    },
    [],
  )
  /** Runs when this session's rule was written but the flow did not finish
   *  (the builder closed after step 1). Set per session by `begin`. */
  const onWritten = useRef<(() => void) | null>(null)


  function finish() {
    reset()
    onDone()
  }

  function savedToast(name: string) {
    toast.show({ kind: 'success', message: created.current ? `Created “${name}”` : `Saved “${name}”` })
  }

  /** The toast for a write whose response may carry a batch: what it changed,
   *  or, when it changed nothing, that the rule was saved. */
  function toastApplied(result: SaveResult) {
    if (!announceBackfill(result)) savedToast(result.rule.rule.name)
  }

  /** Report the rule this session created, with what is now STORED. */
  function flushCreated(stored: boolean) {
    const props = pendingCreated.current
    pendingCreated.current = null
    if (props !== null) track('rule_created', { ...props, apply_to_past_enabled: stored })
  }

  function endSession() {
    // A session ending with the creation unreported means the rule stands as
    // step 1 wrote it: without past.
    flushCreated(false)
    session.current += 1
    written.current = null
    created.current = false
    onWritten.current = null
  }

  /** A builder is opening, new or edit: it starts with nothing written.
   *  `onWrittenUncounted` fires if the rule is written and the session ends unfinished. */
  function begin(onWrittenUncounted?: () => void) {
    endSession()
    onWritten.current = onWrittenUncounted ?? null
  }

  /** Forget the builder session and any flow in it, silently. */
  function reset() {
    setStep(IDLE)
    setOffer(null)
    endSession()
  }

  /** The builder closing without finishing. If step 1 already wrote the rule,
   *  that write stands — undoing it is not ours to decide — and the user is
   *  told the past was not touched. */
  function close() {
    const w = written.current
    const cb = onWritten.current
    if (w !== null) toast.show({ kind: 'info', message: withoutPastCopy(w.rule.name, created.current) })
    if (w !== null && cb !== null) told.current.add(session.current)
    reset()
    if (w !== null) cb?.()
  }

  async function save(
    draft: RuleDraft,
    extra: { priority?: number; renumber?: boolean; from?: ReadRule | null },
  ) {
    const mine = session.current
    const onWrittenUncounted = onWritten.current
    const ended = () => session.current !== mine
    const today = localToday()
    const start = clampStart(draft.backfillStart, today)
    const base = written.current ?? extra.from ?? null
    const isNew = base === null
    const ask = needsConfirm(draft, base)
    const full = toWrite(draft)
    // An edit re-sends conditions and actions ONLY when the user changed them.
    // `toWrite` re-serialises (lists re-joined, values trimmed), and the
    // server compares content — so re-sending an untouched "Uber, Lyft" as
    // "Uber,Lyft" would read as a change and backfill a rename unasked.
    // Both sides go through `toWrite`, so an untouched rule compares equal.
    const was = base?.editable ? toWrite(base.draft) : null
    const { conditions, actions, ...rest } = full
    const unchanged = was !== null && same(conditions, was.conditions) && same(actions, was.actions)
    const body = {
      ...(unchanged ? rest : full),
      // Step 1 of the sequence writes nothing to history; any other save
      // stores the draft's intent as it stands.
      apply_to_past: ask ? false : draft.applyToPast,
      backfill_start_date: start,
    }
    if (isNew && atRuleCap(client.getQueryData<ReadRule[]>(keys.rules.list())?.length)) {
      // Whoever opened the builder, a 1001st rule is refused here — leaving the step exactly as the recount's
      // refusal does, so the same refusal never reads two ways.
      setStep({ k: 'failed', message: RULE_CAP_REASON, saved: false })
      throw new SaveError(RULE_CAP_REASON, false)
    }
    setStep({ k: 'saving' })
    let result: SaveResult
    let renumbered = false
    try {
      if (isNew && extra.renumber === true) {
        // "Top" with the highest rule already at 1000: renumber the existing
        // list to n…1 in ONE bulk PATCH, so the create below lands strictly above.
        // staleTime 0: a rule added or deleted elsewhere inside the app's 30s
        // cache window would otherwise be missed (still tied) or 404 the PATCH.
        const current = await client.fetchQuery({
          queryKey: keys.rules.list(),
          queryFn: listAllRules,
          staleTime: 0,
        })
        // The cache count above may be behind: recount on what the server has.
        if (atRuleCap(current.length)) throw new SaveError(RULE_CAP_REASON, false)
        renumbered = true
        await renumberRules(current.map((r) => r.rule.id))
      }
      result = isNew
        ? await create.mutateAsync({ ...full, ...body, priority: extra.priority })
        : await update.mutateAsync({ id: base.rule.id, body })
    } catch (e) {
      // A renumber that landed before the create failed leaves the cache with
      // the old priorities; make it read the server's.
      if (renumbered) void client.invalidateQueries({ queryKey: keys.rules.all() })
      // A SaveError (the cap refusal) carries its own message and is never "no answer".
      let message = e instanceof SaveError ? e.message : saveFailureCopy(e, body.name)
      if (!(e instanceof SaveError) && mayHaveCommitted(e)) {
        // The write may have landed after the client gave up (the server's limit is longer than ours).
        // Re-read, so the list shows the rule if it was created. Nothing is adopted: the server refuses a
        // second rule of one name (a fast 409, backend rules.py:187-194), so a retried create can never
        // duplicate it or backfill twice, and a name match could just as well be someone else's rule.
        // `retry: false`: the failure message does not wait on the re-read.
        await client
          .fetchQuery({ queryKey: keys.rules.list(), queryFn: listAllRules, staleTime: 0, retry: false })
          .catch(() => null)
        void client.invalidateQueries({ queryKey: keys.rules.all() })
      }
      if (ended()) return
      setStep({ k: 'failed', message, saved: false })
      throw new SaveError(message, false)
    }
    // Shape only — counts and a boolean. Never the name or a condition value.
    const shape = {
      condition_count: draft.groups.reduce((n, g) => n + g.conditions.length, 0),
      group_count: draft.groups.length,
      action_count: draft.actions.length,
    }
    const stored = result.rule.rule.apply_to_past
    if (!isNew) track('rule_edited', { ...shape, apply_to_past_enabled: stored })
    else if (!ask) track('rule_created', { ...shape, apply_to_past_enabled: stored })
    if (ended()) {
      if (isNew && ask) track('rule_created', { ...shape, apply_to_past_enabled: stored })
      // The builder closed before the write returned, so `close` had nothing
      // to report. The rule is written: say what became of it, and leave the
      // session that replaced this one alone.
      if (!ask) toastApplied(result)
      else toast.show({ kind: 'info', message: withoutPastCopy(result.rule.rule.name, isNew) })
      if (!told.current.has(mine)) onWrittenUncounted?.()
      return
    }
    written.current = result.rule
    if (isNew) created.current = true
    if (isNew && ask) pendingCreated.current = shape
    const name = result.rule.rule.name
    const done = (r: SaveResult) => {
      flushCreated(r.rule.rule.apply_to_past)
      toastApplied(r)
      finish()
    }
    if (!ask) return done(result)

    setStep({ k: 'counting' })
    let held: Props | null = null
    let flipping = false
    let reason: unknown
    try {
      const id = result.rule.rule.id
      const counted = await fetchBackfillPreview(id, start)
      // Ended while counting. The rule was already `written`, so whatever
      // ended the session saw it: `close` told the user; a delete's `reset`
      // made the edit moot; a builder opening again (`begin`) left the write
      // standing. The switch's flow has its own state and cannot end this.
      if (ended()) return
      if (counted.count === 0) {
        // Nothing to write: turn the flag on, so the rule stays one that
        // applies to past, and finish as a plain save.
        // Held out of the session's hands while the flip runs: a session that
        // ends mid-flip must not report false for what is about to be stored true.
        held = pendingCreated.current
        pendingCreated.current = null
        flipping = true
        const flipped = await update.mutateAsync({
          id,
          body: { apply_to_past: true, backfill_start_date: start },
        })
        if (held !== null) track('rule_created', { ...held, apply_to_past_enabled: flipped.rule.rule.apply_to_past })
        held = null
        if (ended()) return void announceBackfill(flipped)
        written.current = flipped.rule
        return done(flipped)
      }
      const confirm: ConfirmStep = {
        k: 'confirm',
        id,
        ...counted,
        from: windowStart(start, today),
        defaulted: start === null,
      }
      setOffer({ ...confirm, via: 'save', raised: shared.raised(), name, categoryId: categoryOf(result.rule), start })
      setStep(confirm)
    } catch (e) {
      reason = e
      if (held !== null) {
        // The flip failed: the rule stands with the flag off.
        if (ended()) track('rule_created', { ...held, apply_to_past_enabled: false })
        else pendingCreated.current = held
      }
      // The count is a read; only the flip can have changed history.
      const message = flipping && mayHaveCommitted(reason) ? NOT_CONFIRMED_APPLY : NOT_APPLIED
      if (flipping && mayHaveCommitted(reason)) void client.invalidateQueries({ queryKey: keys.rules.all() })
      if (ended()) return
      setStep({ k: 'failed', message, saved: true })
      throw new SaveError(message, true)
    }
  }

  /** Step 4 from the builder's save — and "Try again" after it failed. */
  async function confirmApply(o: Offer) {
    const mine = session.current
    const wasCreated = created.current
    // The builder's creation event, in hand while the apply runs: a session
    // that ends mid-apply (the page unmounting) must not report `false` for
    // what is about to be stored `true`.
    const held = pendingCreated.current
    pendingCreated.current = null
    setStep({ k: 'applying' })
    let result: SaveResult
    try {
      result = await update.mutateAsync({
        id: o.id,
        body: { apply_to_past: true, backfill_start_date: o.start },
      })
    } catch (e) {
      if (mayHaveCommitted(e)) void client.invalidateQueries({ queryKey: keys.rules.all() })
      // The apply failed: the rule stands with the flag off. A live session
      // keeps the event for "Try again"; an ended one reports it now.
      if (held !== null) {
        if (session.current !== mine) track('rule_created', { ...held, apply_to_past_enabled: false })
        else pendingCreated.current = held
      }
      if (session.current !== mine) {
        // Nobody is left to show a failed step, and the user clicked "Save and apply": tell them what became of
        // it, as `save` does when its session ends. No answer cannot say the past was untouched.
        toast.show(
          mayHaveCommitted(e)
            ? { kind: 'error', message: NOT_CONFIRMED_APPLY }
            : { kind: 'warning', message: withoutPastCopy(o.name, wasCreated) },
        )
        return
      }
      setStep({ k: 'failed', message: mayHaveCommitted(e) ? NOT_CONFIRMED_APPLY : NOT_APPLIED, saved: true })
      return
    }
    // The write has settled: nothing below can turn it into a failure.
    track('rule_backfill_confirmed', { transaction_count: o.count, at_least_shown: o.atLeast })
    if (held !== null) track('rule_created', { ...held, apply_to_past_enabled: result.rule.rule.apply_to_past })
    toastApplied(result)
    // The session ended while the apply ran (the page left, or `close` ran): its `onWritten`
    // already told the caller, and `onDone` would tell it again — a second dismissal of one suggestion.
    if (session.current !== mine) return
    finish()
  }

  /** The rule is already saved with the flag off: nothing more is written to
   *  history. */
  function skipSave(o: Offer) {
    savedToast(o.name)
    finish()
  }

  /** Back to the builder. A saved rule stays saved, with the flag off. */
  function back() {
    setStep(IDLE)
    setOffer(null)
  }

  return { step, offer, save, confirmApply, skipSave, back, begin, close, reset }
}
